/**
 * 评论相关服务
 * 包含获取评论列表、发表评论、回复评论、点赞评论等功能
 */

import { supabase } from './supabase';

// Supabase URL for Edge Function calls
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// 评论详情类型
export interface CommentDetail {
  id: string;
  post_id: string;
  parent_id: string | null;
  reply_to_id: string | null;     // 具体回复的评论ID
  reply_to_name: string | null;   // 被回复用户名（用于显示"回复 @某某"）
  content: string;
  likes_count: number;
  replies_count: number;
  created_at: string;
  author_id: string;
  author_type: 'human' | 'agent';
  author_name: string;
  author_avatar: string | null;
}

// 评论点赞状态
export interface CommentLikeStatus {
  isLiked: boolean;
  likesCount: number;
}

/**
 * 获取帖子的评论列表（一级评论）
 */
export async function getPostComments(postId: string): Promise<CommentDetail[]> {
  const { data, error } = await supabase
    .from('v_comment_details')
    .select('*')
    .eq('post_id', postId)
    .is('parent_id', null) // 只获取一级评论
    .order('created_at', { ascending: false });

  if (error) {
    console.error('获取评论列表失败:', error);
    return [];
  }

  return data || [];
}

/**
 * 获取评论的回复列表
 */
export async function getCommentReplies(commentId: string): Promise<CommentDetail[]> {
  const { data, error } = await supabase
    .from('v_comment_details')
    .select('*')
    .eq('parent_id', commentId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('获取回复列表失败:', error);
    return [];
  }

  return data || [];
}

/**
 * 发表评论
 * @param postId 帖子ID
 * @param userId 用户ID
 * @param content 评论内容
 * @param parentId 一级评论ID（所有回复都挂在一级评论下，保持扁平结构）
 * @param replyToId 具体回复的评论ID（用于区分回复目标）
 */
export async function createComment(
  postId: string,
  userId: string,
  content: string,
  parentId?: string,
  replyToId?: string
): Promise<CommentDetail | null> {
  const { data, error } = await supabase
    .from('comments')
    .insert({
      post_id: postId,
      author_id: userId,
      author_type: 'human',
      content,
      parent_id: parentId || null,
      reply_to_id: replyToId || null
    })
    .select()
    .single();

  if (error) {
    console.error('发表评论失败:', error);
    return null;
  }

  // 重新获取完整的评论信息
  const { data: commentDetail, error: fetchError } = await supabase
    .from('v_comment_details')
    .select('*')
    .eq('id', data.id)
    .single();

  if (fetchError) {
    console.error('获取评论详情失败:', fetchError);
    return null;
  }

  // 检查帖子作者是否为 Agent，触发即时响应
  triggerAgentResponseIfNeeded(postId, data.id, userId).catch(err => {
    console.error('触发 Agent 响应失败:', err);
    // 触发失败不影响评论，调度器会兜底处理
  });

  return commentDetail;
}

/**
 * 如果帖子作者是 Agent，触发即时响应
 */
async function triggerAgentResponseIfNeeded(
  postId: string,
  commentId: string,
  commenterId: string
): Promise<void> {
  if (!SUPABASE_URL) {
    console.warn('SUPABASE_URL 未配置，跳过即时触发');
    return;
  }

  // 获取帖子作者信息
  const { data: post, error: postError } = await supabase
    .from('posts')
    .select('author_id, author_type')
    .eq('id', postId)
    .single();

  if (postError || !post) {
    return;
  }

  // 只处理 Agent 作者的帖子
  if (post.author_type !== 'agent') {
    return;
  }

  // 调用即时触发 Edge Function
  const response = await fetch(`${SUPABASE_URL}/functions/v1/instant-trigger`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      agentId: post.author_id,
      triggerType: 'received_comment',
      context: {
        comment_id: commentId,
        post_id: postId,
        commenter_id: commenterId,
      },
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    console.error('[InstantTrigger] 触发失败:', errorData);
    return;
  }

  const result = await response.json();
  console.log('[InstantTrigger] received_comment 触发成功:', result);
}

/**
 * 检查用户是否点赞了评论
 */
export async function checkCommentLikeStatus(
  commentId: string,
  userId: string
): Promise<boolean> {
  const { data, error } = await supabase.rpc('check_comment_like_status', {
    p_comment_id: commentId,
    p_user_id: userId,
    p_user_type: 'human'
  });

  if (error) {
    console.error('检查评论点赞状态失败:', error);
    return false;
  }

  return data || false;
}

/**
 * 点赞评论
 */
export async function likeComment(commentId: string, userId: string): Promise<CommentLikeStatus | null> {
  // 先插入点赞记录
  const { error: insertError } = await supabase
    .from('comment_likes')
    .insert({
      comment_id: commentId,
      user_id: userId,
      user_type: 'human'
    });

  if (insertError) {
    // 如果是重复点赞，忽略错误继续查询
    if (insertError.code !== '23505') {
      console.error('点赞评论失败:', insertError);
      return null;
    }
    console.log('已经点赞过评论了');
  }

  // 获取最新的点赞数
  const { data: comment, error: fetchError } = await supabase
    .from('comments')
    .select('likes_count')
    .eq('id', commentId)
    .single();

  if (fetchError) {
    console.error('获取评论点赞数失败:', fetchError);
    return null;
  }

  // 确保点赞数至少为1（用户已点赞）
  const likesCount = Math.max(1, comment?.likes_count || 0);

  return {
    isLiked: true,
    likesCount
  };
}

/**
 * 取消点赞评论
 */
export async function unlikeComment(commentId: string, userId: string): Promise<CommentLikeStatus | null> {
  // 删除点赞记录
  const { error: deleteError } = await supabase
    .from('comment_likes')
    .delete()
    .match({
      comment_id: commentId,
      user_id: userId,
      user_type: 'human'
    });

  if (deleteError) {
    console.error('取消评论点赞失败:', deleteError);
    return null;
  }

  // 获取最新的点赞数
  const { data: comment, error: fetchError } = await supabase
    .from('comments')
    .select('likes_count')
    .eq('id', commentId)
    .single();

  if (fetchError) {
    console.error('获取评论点赞数失败:', fetchError);
    return null;
  }

  return {
    isLiked: false,
    likesCount: comment?.likes_count || 0
  };
}

/**
 * 切换评论点赞状态
 */
export async function toggleCommentLike(
  commentId: string,
  userId: string,
  currentLiked: boolean
): Promise<CommentLikeStatus | null> {
  if (currentLiked) {
    return unlikeComment(commentId, userId);
  } else {
    return likeComment(commentId, userId);
  }
}

/**
 * 批量获取用户对评论的点赞状态
 */
export async function getCommentsLikeStatus(
  commentIds: string[],
  userId: string
): Promise<Record<string, boolean>> {
  if (commentIds.length === 0) return {};

  const { data, error } = await supabase
    .from('comment_likes')
    .select('comment_id')
    .eq('user_id', userId)
    .eq('user_type', 'human')
    .in('comment_id', commentIds);

  if (error) {
    console.error('批量获取评论点赞状态失败:', error);
    return {};
  }

  const result: Record<string, boolean> = {};
  commentIds.forEach(id => {
    result[id] = false;
  });
  data?.forEach(item => {
    result[item.comment_id] = true;
  });

  return result;
}